/**
 * Clinical record sync utility.
 *
 * Reads FHIR clinical records from the HealthKit native module and sends
 * them to the backend, which normalises them with the same Fasten FHIR
 * layer and stores them as `type = 'fhir-resource'` notes.
 *
 * This runs once per session per project (clinical records change rarely).
 */

import { NativeModules, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { store } from '../store';
import debug from './debug';

const THROTTLE_KEY_PREFIX = '@eleplan/clinical_last_sync_';
const THROTTLE_MS = 24 * 60 * 60 * 1000; // once per 24 hours per project

interface ClinicalRecord {
  resourceType: string;
  id: string;
  fhirJson: string;
}

function getApiBase(): string {
  const { default: cfg } = require('../config');
  return cfg?.apiGateway?.endpoint ?? '';
}

function getAuthToken(): string | null {
  const state = store.getState();
  return (state as any)?.session?.token ?? null;
}

function getSessionIds(): { accountId: string | null; cognitoId: string | null } {
  const state = store.getState();
  return {
    accountId: (state as any)?.session?.account?.account_id ?? null,
    cognitoId: (state as any)?.authentication?.user?.cognito_id ?? null,
  };
}

/**
 * Syncs clinical records for a single project.
 * Throttled to once every 24 hours per project.
 * No-ops silently if not iOS, no native module, or throttle active.
 */
export async function syncClinicalRecords(projectId: number | string): Promise<void> {
  if (Platform.OS !== 'ios') return;

  const { HealthClinicalModule } = NativeModules;
  if (!HealthClinicalModule?.queryClinicalRecords) {
    debug.info('documents', 'HealthClinicalModule not available — skipping clinical sync');
    return;
  }

  const throttleKey = `${THROTTLE_KEY_PREFIX}${projectId}`;
  const lastSyncStr = await AsyncStorage.getItem(throttleKey);
  if (lastSyncStr) {
    const lastSync = parseInt(lastSyncStr, 10);
    if (Date.now() - lastSync < THROTTLE_MS) return;
  }

  const { accountId, cognitoId } = getSessionIds();
  const token = getAuthToken();
  if (!accountId || !cognitoId || !token) return;

  try {
    const records: ClinicalRecord[] = await HealthClinicalModule.queryClinicalRecords();
    if (!records || records.length === 0) {
      debug.info('documents', 'No clinical records found in HealthKit');
      return;
    }

    debug.info('documents', 'Syncing clinical records', { count: records.length, projectId });

    // Parse each fhirJson string before sending
    const resources = records
      .map((r: ClinicalRecord) => {
        try {
          return JSON.parse(r.fhirJson);
        } catch {
          return null;
        }
      })
      .filter(Boolean);

    if (resources.length === 0) return;

    const apiBase = getApiBase();
    const response = await fetch(
      `${apiBase}/health/sync-clinical/${projectId}/${accountId}/${cognitoId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ resources }),
      },
    );

    const data = await response.json();
    if (data?.success) {
      await AsyncStorage.setItem(throttleKey, String(Date.now()));
      debug.info('documents', 'Clinical records synced', {
        synced: data.synced,
        skipped: data.skipped,
      });
    }
  } catch (err: any) {
    debug.warn('Clinical record sync failed', { message: err?.message });
  }
}
